import React, { useState, useEffect, useCallback } from 'react';
import {
  getMembers, createMember, updateMember, generateMembersCSV,
  MEMBER_WIDOW_STAGES, MEMBER_EMPLOYMENT_STATUSES, MEMBER_INTERESTS,
  SINGAPORE_DISTRICTS, calculateEngagementScore, fmtDate,
} from '../../../api/communityApi';

// ── Helpers ────────────────────────────────────────────────────────────────

function EngagementBadge({ tier }) {
  const cls = `bb-com-eng-${tier || 'low'}`;
  const labels = { high: 'High', medium: 'Medium', low: 'Low', at_risk: 'Low' };
  return <span className={cls}>{labels[tier] || tier}</span>;
}

function WidowStageBadge({ stage }) {
  if (!stage) return null;
  const cls = stage === 'current' ? 'bb-com-widow-current' : 'bb-com-widow-past';
  const found = MEMBER_WIDOW_STAGES.find(s => s.value === stage);
  return <span className={cls}>{found ? found.label : stage}</span>;
}

// ── Edit Member Modal ──────────────────────────────────────────────────────

function EditMemberModal({ member, orgId, onClose, onSaved }) {
  const [form, setForm] = useState({
    displayName:      member.displayName || '',
    email:            member.email || '',
    phone:            member.phone || '',
    district:         member.district || '',
    widowStage:       member.widowStage || '',
    employmentStatus: member.employmentStatus || '',
    interests:        member.interests || [],
    notes:            member.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const toggleInterest = (val) => {
    setForm(f => ({
      ...f,
      interests: f.interests.includes(val)
        ? f.interests.filter(x => x !== val)
        : [...f.interests, val],
    }));
  };

  const handleSave = async () => {
    if (!form.displayName.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await updateMember(orgId, member.id, form);
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const scoreData = calculateEngagementScore(member);

  return (
    <div className="bb-com-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-com-modal" style={{ maxWidth: 540 }}>
        <h3>Edit Member — {member.displayName}</h3>
        {error && <div className="bb-alert bb-alert--error" style={{ margin: '0 0 12px' }}>{error}</div>}

        <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          Engagement Score: <strong style={{ color: '#4338CA' }}>{scoreData.score}</strong>
          &nbsp;— <EngagementBadge tier={scoreData.tier} />
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
            Event: {scoreData.eventScore} | Recency: {scoreData.recencyScore} | Program: {scoreData.progScore}
          </div>
        </div>

        <div className="bb-form-row">
          <div className="bb-form-group">
            <label>Full Name <span className="bb-required">*</span></label>
            <input className="bb-input" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
          </div>
          <div className="bb-form-group">
            <label>Email</label>
            <input className="bb-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
        </div>

        <div className="bb-form-row">
          <div className="bb-form-group">
            <label>Phone</label>
            <input className="bb-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          </div>
          <div className="bb-form-group">
            <label>District</label>
            <select className="bb-select" value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))}>
              <option value="">— Select —</option>
              {SINGAPORE_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        <div className="bb-form-row">
          <div className="bb-form-group">
            <label>Widow Stage</label>
            <select className="bb-select" value={form.widowStage} onChange={e => setForm(f => ({ ...f, widowStage: e.target.value }))}>
              <option value="">— Select —</option>
              {MEMBER_WIDOW_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="bb-form-group">
            <label>Employment Status</label>
            <select className="bb-select" value={form.employmentStatus} onChange={e => setForm(f => ({ ...f, employmentStatus: e.target.value }))}>
              <option value="">— Select —</option>
              {MEMBER_EMPLOYMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="bb-form-group">
          <label>Interests</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {MEMBER_INTERESTS.map(i => (
              <label key={i.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <input type="checkbox" checked={form.interests.includes(i.value)} onChange={() => toggleInterest(i.value)} />
                {i.label}
              </label>
            ))}
          </div>
        </div>

        <div className="bb-form-group">
          <label>Admin Notes</label>
          <textarea className="bb-textarea" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>

        <div className="bb-com-modal-footer">
          <button className="bb-btn-outline-sm" onClick={onClose}>Cancel</button>
          <button className="bb-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Engagement Details Modal ───────────────────────────────────────────────

function EngagementModal({ member, onClose }) {
  const scoreData = calculateEngagementScore(member);
  return (
    <div className="bb-com-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-com-modal" style={{ maxWidth: 380 }}>
        <h3>Engagement Score — {member.displayName}</h3>
        <div style={{ fontSize: 32, fontWeight: 700, color: '#4338CA', textAlign: 'center', margin: '16px 0 4px' }}>
          {scoreData.score}
        </div>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <EngagementBadge tier={scoreData.tier} />
        </div>
        <table className="bb-com-table">
          <tbody>
            <tr><td>Event Attendance (×0.5)</td><td style={{ fontWeight: 700, textAlign: 'right' }}>{scoreData.eventScore}</td></tr>
            <tr><td>Recency (×0.3)</td><td style={{ fontWeight: 700, textAlign: 'right' }}>{scoreData.recencyScore}</td></tr>
            <tr><td>Program Score (×0.2)</td><td style={{ fontWeight: 700, textAlign: 'right' }}>{scoreData.progScore}</td></tr>
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 12 }}>
          Last active: {fmtDate(member.lastActiveAt)}
        </div>
        <div className="bb-com-modal-footer">
          <button className="bb-btn-outline-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── All Members Tab ────────────────────────────────────────────────────────

function AllMembersTab({ members, orgId, adminId, onRefresh }) {
  const [search, setSearch]         = useState('');
  const [editing, setEditing]       = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [viewEngage, setViewEngage] = useState(null);

  const filtered = members.filter(m => {
    const q = search.toLowerCase();
    return (
      (m.displayName || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <>
      {editing && (
        <EditMemberModal
          member={editing}
          orgId={orgId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
      {viewEngage && (
        <EngagementModal member={viewEngage} onClose={() => setViewEngage(null)} />
      )}

      {showCreate && (
        <div className="bb-com-modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="bb-com-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Create Member</h3>
              <button className="bb-btn-outline-sm" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <CreateMemberForm orgId={orgId} adminId={adminId} onCreated={() => { setShowCreate(false); onRefresh(); }} />
          </div>
        </div>
      )}

      <div className="bb-com-search">
        <input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={{ fontSize: 12, color: '#9CA3AF', alignSelf: 'center' }}>
          {filtered.length} member(s)
        </span>
        <button className="bb-btn-primary" style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }} onClick={() => setShowCreate(true)}>
          + Create Member
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="bb-com-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>District</th>
              <th>Stage</th>
              <th>Employment</th>
              <th>Engagement</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No members found.</td></tr>
            )}
            {filtered.map(m => {
              const empFound = MEMBER_EMPLOYMENT_STATUSES.find(s => s.value === m.employmentStatus);
              return (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600 }}>{m.displayName}</td>
                  <td>{m.email || '—'}</td>
                  <td>{m.phone || '—'}</td>
                  <td>{m.district || '—'}</td>
                  <td><WidowStageBadge stage={m.widowStage} /></td>
                  <td style={{ fontSize: 12 }}>{empFound ? empFound.label : (m.employmentStatus || '—')}</td>
                  <td><EngagementBadge tier={m.engagementTier} /></td>
                  <td>{fmtDate(m.createdAt)}</td>
                  <td>
                    <div className="bb-action-row">
                      <button className="bb-btn-outline-sm" onClick={() => setEditing(m)}>Edit</button>
                      <button className="bb-btn-sm" onClick={() => setViewEngage(m)}>Engagement</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Create Member Form ─────────────────────────────────────────────────────

function CreateMemberForm({ orgId, adminId, onCreated }) {
  const empty = {
    displayName: '', email: '', phone: '', district: '',
    widowStage: '', employmentStatus: '', interests: [], notes: '',
  };
  const [form, setForm]       = useState(empty);
  const [errors, setErrors]   = useState({});
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState('');
  const [apiError, setApiError] = useState('');

  const toggleInterest = (val) => {
    setForm(f => ({
      ...f,
      interests: f.interests.includes(val)
        ? f.interests.filter(x => x !== val)
        : [...f.interests, val],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.displayName.trim()) e.displayName = 'Name is required.';
    if (!form.email.trim())       e.email = 'Email is required.';
    return e;
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    setApiError('');
    try {
      await createMember(orgId, form, adminId);
      setSuccess('Member created successfully!');
      setForm(empty);
      setErrors({});
      onCreated && onCreated();
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setApiError(e.message || 'Failed to create member.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {success  && <div className="bb-alert bb-alert--info"  style={{ margin: '0 0 16px' }}>{success}</div>}
      {apiError && <div className="bb-alert bb-alert--error" style={{ margin: '0 0 16px' }}>{apiError}</div>}

      <div className="bb-form-row">
        <div className="bb-form-group">
          <label>Full Name <span className="bb-required">*</span></label>
          <input
            className={`bb-input${errors.displayName ? ' bb-input--error' : ''}`}
            value={form.displayName}
            onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
          />
          {errors.displayName && <span className="bb-field-error">{errors.displayName}</span>}
        </div>
        <div className="bb-form-group">
          <label>Email <span className="bb-required">*</span></label>
          <input
            className={`bb-input${errors.email ? ' bb-input--error' : ''}`}
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
          {errors.email && <span className="bb-field-error">{errors.email}</span>}
        </div>
      </div>

      <div className="bb-form-row">
        <div className="bb-form-group">
          <label>Phone</label>
          <input className="bb-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
        </div>
        <div className="bb-form-group">
          <label>District</label>
          <select className="bb-select" value={form.district} onChange={e => setForm(f => ({ ...f, district: e.target.value }))}>
            <option value="">— Select —</option>
            {SINGAPORE_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div className="bb-form-row">
        <div className="bb-form-group">
          <label>Widow Stage</label>
          <select className="bb-select" value={form.widowStage} onChange={e => setForm(f => ({ ...f, widowStage: e.target.value }))}>
            <option value="">— Select —</option>
            {MEMBER_WIDOW_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="bb-form-group">
          <label>Employment Status</label>
          <select className="bb-select" value={form.employmentStatus} onChange={e => setForm(f => ({ ...f, employmentStatus: e.target.value }))}>
            <option value="">— Select —</option>
            {MEMBER_EMPLOYMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="bb-form-group">
        <label>Interests</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {MEMBER_INTERESTS.map(i => (
            <label key={i.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={form.interests.includes(i.value)} onChange={() => toggleInterest(i.value)} />
              {i.label}
            </label>
          ))}
        </div>
      </div>

      <div className="bb-form-group">
        <label>Admin Notes</label>
        <textarea className="bb-textarea" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        <span className="bb-hint">Financial state and other sensitive fields are managed separately by admins.</span>
      </div>

      <button type="submit" className="bb-btn-primary" disabled={saving}>
        {saving ? 'Creating…' : 'Create Member'}
      </button>
    </form>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function triggerCSVDownload(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Engagement Dashboard Tab ───────────────────────────────────────────────

function EngagementDashboardTab({ members }) {
  const tiers = {
    high:   members.filter(m => m.engagementTier === 'high'),
    medium: members.filter(m => m.engagementTier === 'medium'),
    low:    members.filter(m => m.engagementTier === 'low' || m.engagementTier === 'at_risk'),
  };

  const sorted = [...members].sort((a, b) => (b.engagementScore || 0) - (a.engagementScore || 0));

  const tierConfig = [
    { key: 'high',   label: 'High (≥70)',    color: '#065F46', bg: '#D1FAE5' },
    { key: 'medium', label: 'Medium (40–69)', color: '#1E40AF', bg: '#DBEAFE' },
    { key: 'low',    label: 'Low (<40)',      color: '#92400E', bg: '#FEF3C7' },
  ];

  return (
    <div>
      <div className="bb-com-stats">
        {tierConfig.map(t => (
          <div key={t.key} className="bb-com-stat-card" style={{ borderTop: `3px solid ${t.color}` }}>
            <div className="bb-com-stat-val" style={{ color: t.color }}>{tiers[t.key].length}</div>
            <div className="bb-com-stat-lbl">{t.label}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tierConfig.map(t => (
          <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: t.bg, border: `1px solid ${t.color}` }} />
            <span>{t.label}: {tiers[t.key].length}</span>
          </div>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="bb-com-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Score</th>
              <th>Tier</th>
              <th>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((m, i) => (
              <tr key={m.id}>
                <td style={{ color: '#9CA3AF', fontSize: 12 }}>{i + 1}</td>
                <td style={{ fontWeight: 600 }}>{m.displayName}</td>
                <td>{m.email || '—'}</td>
                <td style={{ fontWeight: 700, color: '#4338CA' }}>{m.engagementScore ?? '—'}</td>
                <td><EngagementBadge tier={m.engagementTier} /></td>
                <td>{fmtDate(m.lastActiveAt)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>No members.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Low Engagement Tab ─────────────────────────────────────────────────────

function LowEngagementTab({ members }) {
  const atRisk = members.filter(m => m.engagementTier === 'low' || m.engagementTier === 'at_risk');

  const getDaysSince = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return Math.round((Date.now() - d.getTime()) / 86400000);
  };

  const handleExport = () => {
    const csv = generateMembersCSV(atRisk);
    triggerCSVDownload(csv, 'low-engagement-members.csv');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#9CA3AF' }}>
          {atRisk.length} member(s) with engagement score &lt; 40
        </div>
        {atRisk.length > 0 && (
          <button className="bb-btn-outline-sm" onClick={handleExport}>Export Low Engagement List</button>
        )}
      </div>

      {atRisk.length === 0 ? (
        <div className="bb-empty-state" style={{ color: '#065F46', background: '#D1FAE5', borderRadius: 10, padding: 32 }}>
          No low-engagement members — great engagement!
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="bb-com-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Score</th>
                <th>Last Active</th>
                <th>Days Since Active</th>
              </tr>
            </thead>
            <tbody>
              {atRisk.map(m => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600 }}>{m.displayName}</td>
                  <td>{m.email || '—'}</td>
                  <td style={{ fontWeight: 700, color: '#991B1B' }}>{m.engagementScore ?? '—'}</td>
                  <td>{fmtDate(m.lastActiveAt)}</td>
                  <td>
                    {(() => {
                      const days = getDaysSince(m.lastActiveAt);
                      const num  = typeof days === 'number' ? days : null;
                      return (
                        <span style={{ fontWeight: 600, color: num && num > 60 ? '#991B1B' : '#92400E' }}>
                          {days}{typeof days === 'number' ? ' days' : ''}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function MemberProfiles({ orgId, adminId }) {
  const [activeTab, setActiveTab] = useState('all');
  const [members, setMembers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMembers(orgId);
      setMembers(data);
    } catch (e) {
      setError(e.message || 'Failed to load members.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: 'all',        label: `All Members (${members.length})` },
    { key: 'engagement', label: 'Engagement Dashboard' },
    { key: 'low',        label: `Low (${members.filter(m => m.engagementTier === 'low' || m.engagementTier === 'at_risk').length})` },
  ];

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <h2 className="bb-prog-tool-title">Member Profiles</h2>
        <p className="bb-prog-tool-desc">Manage member profiles, engagement scores, and records.</p>
        <div className="bb-prog-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bb-prog-tab-content">
        {error && <div className="bb-alert bb-alert--error">{error}</div>}
        {loading ? (
          <div className="bb-loading-row">Loading members…</div>
        ) : (
          <>
            {activeTab === 'all' && (
              <AllMembersTab members={members} orgId={orgId} adminId={adminId} onRefresh={load} />
            )}
            {activeTab === 'engagement' && (
              <EngagementDashboardTab members={members} />
            )}
            {activeTab === 'low' && (
              <LowEngagementTab members={members} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
